import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import db, { storage } from './Firebase';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const TABS = [
    "Application Form",
    "Declaration from Parent",
    "FOR OFFICE USE ONLY",
    "Upload Documents"
];

const initialFormState = {
    applicantName: '', course: '', batch: '', dob: '', doa: '', age: '', gender: '', qualification: '', contact1: '', address: '',
    fatherName: '', contact2: '', fatherOccupation: '', motherName: '', motherOccupation: '', familySize: '', familyType: '',
    income: '', group: '', documents: [], jobPreference: '', date: '', photo: null, signature: null,
    declarationName: '', declarationChildName: '', declarationCourse: '', declarationDate: '', declarationSignature: null,
    centre: "", referredBy: "", regNo: "", batchNo: "", docsSubmitted: "", screenSchedule: "", officeDate: "", inChargeName: "", sswa: "",
    sswp: "",
    inChargeSignature: null, approver: "", status: "Pending", remark: "",
    uploadDoc1: null, uploadDoc2: null, uploadDoc3: null, uploadDoc4: null, uploadDoc5: null, uploadDoc6: null,
};

const TabFormEdit = () => {
    const { id } = useParams();
    const [formData, setFormData] = useState(initialFormState);
    const [currentTab, setCurrentTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [updateError, setUpdateError] = useState(null);

    useEffect(() => {
        const fetchDoc = async () => {
            try {
                const docRef = doc(db, "registration_form_data", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setFormData(prev => ({ ...prev, ...data }));
                } else {
                    setUpdateError("❌ Document not found.");
                }
            } catch (err) {
                console.error(err);
                setUpdateError("❌ Error fetching data.");
            } finally {
                setLoading(false);
            }
        };
        fetchDoc();
    }, [id]);

    const handleChange = (e) => {
        const { name, value, files, type, selectedOptions } = e.target;
        if (files) {
            setFormData(prev => ({ ...prev, [name]: files[0] }));
        } else if (type === 'select-multiple') {
            const values = Array.from(selectedOptions).map(opt => opt.value);
            setFormData(prev => ({ ...prev, [name]: values }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const uploadFileAndGetURL = async (file, folder = "form_uploads") => {
        if (!file || typeof file === "string") return file;
        const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
        const uploadTask = await uploadBytesResumable(storageRef, file);
        return await getDownloadURL(uploadTask.ref);
    };

    const handleUpdate = async () => {
        try {
            setIsUpdating(true);
            setUpdateSuccess(false);
            setUpdateError(null);

            const uploadTasks = [
                uploadFileAndGetURL(formData.photo),
                uploadFileAndGetURL(formData.signature),
                uploadFileAndGetURL(formData.declarationSignature),
                uploadFileAndGetURL(formData.inChargeSignature),
                uploadFileAndGetURL(formData.uploadDoc1),
                uploadFileAndGetURL(formData.uploadDoc2),
                uploadFileAndGetURL(formData.uploadDoc3),
                uploadFileAndGetURL(formData.uploadDoc4),
                uploadFileAndGetURL(formData.uploadDoc5),
                uploadFileAndGetURL(formData.uploadDoc6),
            ];

            const [
                photoURL, signatureURL, declarationSignatureURL, inChargeSignatureURL,
                doc1URL, doc2URL, doc3URL, doc4URL, doc5URL, doc6URL
            ] = await Promise.all(uploadTasks);

            const payload = {
                ...formData,
                photo: photoURL,
                signature: signatureURL,
                declarationSignature: declarationSignatureURL,
                inChargeSignature: inChargeSignatureURL,
                uploadDoc1: doc1URL,
                uploadDoc2: doc2URL,
                uploadDoc3: doc3URL,
                uploadDoc4: doc4URL,
                uploadDoc5: doc5URL,
                uploadDoc6: doc6URL,
                updatedAt: serverTimestamp(),
            };

            const docRef = doc(db, "registration_form_data", id);
            await updateDoc(docRef, payload);

            setUpdateSuccess(true);
        } catch (error) {
            console.error(error);
            setUpdateError("❌ Error updating document.");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleNext = () => {
        if (currentTab < TABS.length - 1) setCurrentTab(prev => prev + 1);
    };
    const handlePrev = () => {
        if (currentTab > 0) setCurrentTab(prev => prev - 1);
    };

    if (loading) return <div>⏳ Loading form...</div>;

    return (
        <div style={{ maxWidth: "auto", margin: '0 auto', padding: 20, fontFamily: 'Arial' }}>
            <h2 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "1rem" }}>Edit Form</h2>

            {/* Status indicators */}
            {isUpdating && <div style={{ color: "#555", fontWeight: "bold" }}>⏳ Uploading files, please wait...</div>}
            {updateSuccess && <div style={{ color: "green", fontWeight: "bold" }}>✅ Form successfully updated!</div>}
            {updateError && <div style={{ color: "red", fontWeight: "bold" }}>{updateError}</div>}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {TABS.map((tab, idx) => (
                    <button key={idx}
                        onClick={() => setCurrentTab(idx)}
                        style={{
                            flex: 1,
                            padding: "10px",
                            background: currentTab === idx ? "#4A90E2" : "#ccc",
                            color: "#fff",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer"
                        }}>
                        {tab}
                    </button>
                ))}
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
                {/* TAB 1 */}
                {currentTab === 0 && (
                    <div className="card p-4 shadow-sm">
                        <h4 className="mb-4">Application Form</h4>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label htmlFor="applicantName" style={{ display: "flex" }}>Applicant Name</label>
                                <input type="text" name="applicantName" id="applicantName" value={formData.applicantName} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="course" style={{ display: "flex" }}>Course Applied For</label>
                                <select name="course" id="course" value={formData.course} onChange={handleChange} className="form-select">
                                    <option value="">Select Course</option>
                                    <option>Retail Sales Assistant</option>
                                    <option>Warehouse Associate</option>
                                    <option>Digital Mitra</option>
                                    <option>IT Customer Care Executive</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="batch" style={{ display: "flex" }}>Preferred Batch</label>
                                <select name="batch" id="batch" value={formData.batch} onChange={handleChange} className="form-select">
                                    <option value="">Select Batch</option>
                                    <option>Morning</option>
                                    <option>Afternoon</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="dob" style={{ display: "flex" }}>Date of Birth</label>
                                <input type="date" name="dob" id="dob" value={formData.dob} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="age" style={{ display: "flex" }}>Age</label>
                                <input type="number" name="age" id="age" value={formData.age} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="gender" style={{ display: "flex" }}>Gender</label>
                                <select name="gender" id="gender" value={formData.gender} onChange={handleChange} className="form-select">
                                    <option value="">Select Gender</option>
                                    <option>Male</option>
                                    <option>Female</option>
                                    <option>Other</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="qualification" style={{ display: "flex" }}>Qualification</label>
                                <input type="text" name="qualification" id="qualification" value={formData.qualification} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="contact1" style={{ display: "flex" }}>Contact No.</label>
                                <input type="text" name="contact1" id="contact1" value={formData.contact1} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="address" style={{ display: "flex" }}>Address</label>
                                <input type="text" name="address" id="address" value={formData.address} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="fatherName" style={{ display: "flex" }}>Father’s Name</label>
                                <input type="text" name="fatherName" id="fatherName" value={formData.fatherName} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="contact2" style={{ display: "flex" }}>Father’s Contact</label>
                                <input type="text" name="contact2" id="contact2" value={formData.contact2} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="fatherOccupation" style={{ display: "flex" }}>Father’s Occupation</label>
                                <input type="text" name="fatherOccupation" id="fatherOccupation" value={formData.fatherOccupation} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="motherName" style={{ display: "flex" }}>Mother’s Name</label>
                                <input type="text" name="motherName" id="motherName" value={formData.motherName} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="motherOccupation" style={{ display: "flex" }}>Mother’s Occupation</label>
                                <input type="text" name="motherOccupation" id="motherOccupation" value={formData.motherOccupation} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="familySize" style={{ display: "flex" }}>Family Size</label>
                                <input type="number" name="familySize" id="familySize" value={formData.familySize} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="familyType" style={{ display: "flex" }}>Type of Family</label>
                                <select name="familyType" id="familyType" value={formData.familyType} onChange={handleChange} className="form-select">
                                    <option value="">Select Type</option>
                                    <option>Nuclear</option>
                                    <option>Joint</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="income" style={{ display: "flex" }}>Monthly Income</label>
                                <input type="text" name="income" id="income" value={formData.income} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="group" style={{ display: "flex" }}>Social Group</label>
                                <select name="group" id="group" value={formData.group} onChange={handleChange} className="form-select">
                                    <option value="">Select</option>
                                    <option>SC</option>
                                    <option>ST</option>
                                    <option>OBC</option>
                                    <option>General</option>
                                    <option>Others</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="documents" style={{ display: "flex" }}>Documents Attached</label>
                                <select
                                    name="documents"
                                    id="documents"
                                    multiple
                                    value={formData.documents}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="10th">10th</option>
                                    <option value="12th">12th</option>
                                    <option value="Graduation">Graduation</option>
                                    <option value="Aadhar Card">Aadhar Card</option>
                                    <option value="Pan Card">Pan Card</option>
                                    <option value="Other">Other</option>
                                </select>
                                <small className="text-muted">Selected: {formData.documents.join(", ") || "None"}</small>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="jobPreference" style={{ display: "flex" }}>Employment on Completion</label>
                                <select name="jobPreference" id="jobPreference" value={formData.jobPreference} onChange={handleChange} className="form-select">
                                    <option value="">Select</option>
                                    <option>Yes</option>
                                    <option>No</option>
                                </select>
                            </div>


                            <div className="col-md-4">
                                <label htmlFor="doa" style={{ display: "flex" }}>Date of Application</label>
                                <input type="date" name="doa" id="doa" value={formData.doa} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="photo" style={{ display: "flex" }}>Upload Photo</label>
                                <input type="file" name="photo" id="photo" accept="image/*" onChange={handleChange} className="form-control" />
                                {formData.photo && <small style={{ display: "flex" }} className="text-muted">Selected: <img src={formData.photo} style={{ height: "6rem", }}></img></small>}
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="signature" style={{ display: "flex" }}>Upload Signature</label>
                                <input type="file" name="signature" id="signature" accept="image/*" onChange={handleChange} className="form-control" />
                                {formData.signature && <small style={{ display: "flex" }} className="text-muted">Selected: <img src={formData.signature} style={{ height: "6rem" }}></img></small>}
                            </div>
                        </div>
                    </div>
                )}

                {/* TAB 2 — Declaration */}
                {currentTab === 1 && (
                    <>
                        <h4 className="mb-4">Declaration from Parent</h4>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Parent’s Name</label>
                                <input type="text" name="declarationName" value={formData.declarationName} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Child’s Name</label>
                                <input type="text" name="declarationChildName" value={formData.declarationChildName} onChange={handleChange} className="form-control" />
                            </div>
                            {/* <div className="col-md-4">
                                <label style={{ display: "flex" }}>Course (CSR Program)</label>
                                <input type="text" name="declarationCourse" value={formData.declarationCourse} onChange={handleChange} className="form-control" />
                            </div> */}
                            <div className="col-md-4">
                                <label htmlFor="declarationCourse" style={{ display: "flex" }}>Course (CSR Program)</label>
                                <select name="declarationCourse" id="declarationCourse" value={formData.declarationCourse} onChange={handleChange} className="form-select">
                                    <option value="">Select Course</option>
                                    <option>Retail Sales Assistant</option>
                                    <option>Warehouse Associate</option>
                                    <option>Digital Mitra</option>
                                    <option>IT Customer Care Executive</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Date of Declaration</label>
                                <input type="date" name="declarationDate" value={formData.declarationDate} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Upload Parent’s Signature</label>
                                <input type="file" name="declarationSignature" accept="image/*" onChange={handleChange} className="form-control" />
                                {formData.declarationSignature && <small style={{ display: "flex" }} className="text-muted">Selected: <img src={formData.declarationSignature} style={{ height: "6rem", }}></img></small>}
                            </div>
                        </div>
                    </>
                )}

                {/* TAB 3 — Office Use */}
                {currentTab === 2 && (
                    <div className="card p-4 shadow-sm">
                        <h4 className="mb-4">FOR OFFICE USE ONLY</h4>
                        <div className="row g-3">

                            {/* Training Centre */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Training Centre</label>
                                <select
                                    name="centre"
                                    value={formData.centre}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select Centre</option>
                                    <option value="Mansa">Mansa</option>
                                    <option value="Bhiwandi">Bhiwandi</option>
                                    <option value="Tumkur">Tumkur</option>
                                </select>
                            </div>

                            {/* Referred By */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Referred By</label>
                                <select
                                    name="referredBy"
                                    value={formData.referredBy}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select</option>
                                    <option value="Mobiliser">Mobiliser</option>
                                    <option value="Trainer">Trainer</option>
                                    <option value="Friend">Friend</option>
                                </select>
                            </div>

                            {/* Registration No */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Registration No.</label>
                                <input
                                    type="text"
                                    name="regNo"
                                    value={formData.regNo}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            {/* Batch No */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Batch No.</label>
                                <input
                                    type="text"
                                    name="batchNo"
                                    value={formData.batchNo}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            {/* Photocopy of required documents submitted */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Photocopy of Required Documents Submitted</label>
                                <select
                                    name="docsSubmitted"
                                    value={formData.docsSubmitted}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>

                            {/* Screening Schedule */}
                            {/* <div className="col-md-4">
                                <label style={{ display: "flex" }}>Screening Schedule</label>
                                <select
                                    name="screenSchedule"
                                    value={formData.screenSchedule || ""}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select</option>
                                    <option value="with applicant">With Applicant</option>
                                    <option value="with parents">With Parents</option>
                                </select>
                            </div> */}

                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Screening Schedule - With Applicant</label>
                                <input
                                    type="date"
                                    name="sswa"
                                    value={formData.sswa}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Screening Schedule - With Parent</label>
                                <input
                                    type="date"
                                    name="sswp"
                                    value={formData.sswp}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            {/* Office Date */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Office Date</label>
                                <input
                                    type="date"
                                    name="officeDate"
                                    value={formData.officeDate}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            {/* Centre In‑Charge Name */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Centre In‑Charge Name</label>
                                <input
                                    type="text"
                                    name="inChargeName"
                                    value={formData.inChargeName || ""}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            {/* Upload Centre In‑Charge Signature */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Upload Centre In‑Charge Signature</label>
                                <input
                                    type="file"
                                    name="inChargeSignature"
                                    accept="image/*"
                                    onChange={handleChange}
                                    className="form-control"
                                />
                                {formData.inChargeSignature && (
                                    // <small className="text-muted">
                                    //     Selected: {formData.inChargeSignature.name || "1 file uploaded"}
                                    // </small>
                                    <small style={{ display: "flex" }} className="text-muted">Selected: <img src={formData.inChargeSignature} style={{ height: "6rem", }}></img></small>
                                )}
                            </div>

                        </div>
                    </div>
                )}

                {/* TAB 4 — Upload Documents */}
                {currentTab === 3 && (
                    <div className="card p-4 shadow-sm">
                        <h4 className="mb-4">Upload Documents</h4>
                        <div className="row g-3">
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <div key={i} className="col-md-4">
                                    <label htmlFor={`uploadDoc${i}`} style={{ display: "flex" }}>
                                        Upload Document {i}
                                    </label>
                                    <input
                                        type="file"
                                        name={`uploadDoc${i}`}
                                        id={`uploadDoc${i}`}
                                        accept="image/*,application/pdf"
                                        onChange={handleChange}
                                        className="form-control"
                                    />
                                    {formData[`uploadDoc${i}`] && (
                                        // <small className="text-muted">
                                        //     Selected: {formData[`uploadDoc${i}`].name || "Existing File"}
                                        // </small>
                                        <small style={{ display: "flex" }} className="text-muted">Selected: <img src={formData[`uploadDoc${i}`]} style={{ height: "6rem", }}></img></small>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </form>

            {/* Buttons */}
            <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                {currentTab > 0 && (
                    <button onClick={handlePrev} style={{ padding: "10px 20px", background: "#6c757d", color: "#fff", border: "none", borderRadius: 4 }}>
                        Previous
                    </button>
                )}
                {currentTab < TABS.length - 1 ? (
                    <button onClick={handleNext} style={{ padding: "10px 20px", background: "#17a2b8", color: "#fff", border: "none", borderRadius: 4 }}>
                        Next
                    </button>
                ) : (
                    <button
                        onClick={handleUpdate}
                        disabled={isUpdating}
                        style={{
                            padding: "10px 20px",
                            background: "#28a745",
                            color: "#fff",
                            border: "none",
                            borderRadius: 4,
                            cursor: isUpdating ? "not-allowed" : "pointer"
                        }}>
                        {isUpdating ? "Updating…" : "Update"}
                    </button>
                )}
            </div>
        </div>
    );
};

export default TabFormEdit;


// import React, { useEffect, useState } from 'react';
// import { useParams } from 'react-router-dom';
// import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
// import db, { storage } from './Firebase';
// import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

// const TABS = ["Application Form", "Declaration from Parent", "FOR OFFICE USE ONLY"];

// const LOCAL_STORAGE_KEY = 'multiTabFormData';

// const initialFormState = {
//     applicantName: '', course: '', batch: '', dob: '', doa: '', age: '', gender: '', qualification: '', contact1: '', address: '',
//     fatherName: '', contact2: '', fatherOccupation: '', motherName: '', motherOccupation: '', familySize: '', familyType: '',
//     income: '', group: '', documents: '', jobPreference: '', date: '', photo: null, signature: null,
//     declarationName: '', declarationChildName: '', declarationCourse: '', declarationDate: '', declarationSignature: null,
//     centre: "", referredBy: "", regNo: "", batchNo: "", docsSubmitted: "", screenSchedule: "", officeDate: "", inChargeName: "",
//     inChargeSignature: null, approver: "", status: "Pending", remark: "",
// };

// const TabFormEdit = () => {
//     const { id } = useParams();
//     const [formData, setFormData] = useState(initialFormState);
//     const [currentTab, setCurrentTab] = useState(0);
//     const [loading, setLoading] = useState(true);

//     useEffect(() => {
//         const fetchDoc = async () => {
//             try {
//                 const docRef = doc(db, "registration_form_data", id);
//                 const docSnap = await getDoc(docRef);
//                 if (docSnap.exists()) {
//                     const data = docSnap.data();
//                     setFormData(prev => ({ ...prev, ...data }));
//                 } else {
//                     alert("❌ Document not found.");
//                 }
//             } catch (err) {
//                 console.error(err);
//                 alert("❌ Error fetching data.");
//             } finally {
//                 setLoading(false);
//             }
//         };
//         fetchDoc();
//     }, [id]);

//     const handleChange = (e) => {
//         const { name, value, files } = e.target;
//         if (files) {
//             setFormData(prev => ({ ...prev, [name]: files[0] }));
//         } else {
//             setFormData(prev => ({ ...prev, [name]: value }));
//         }
//     };

//     const uploadFileAndGetURL = async (file, field) => {
//         if (!file || typeof file === "string") return file;
//         const storageRef = ref(storage, `form_uploads/${Date.now()}_${file.name}`);
//         const uploadTask = await uploadBytesResumable(storageRef, file);
//         return await getDownloadURL(uploadTask.ref);
//     };

//     const handleUpdate = async () => {
//         try {
//             alert("⏳ Uploading...");

//             const updatedPhoto = await uploadFileAndGetURL(formData.photo, "photo");
//             const updatedSignature = await uploadFileAndGetURL(formData.signature, "signature");
//             const updatedDeclarationSignature = await uploadFileAndGetURL(formData.declarationSignature, "declarationSignature");
//             const updatedInChargeSignature = await uploadFileAndGetURL(formData.inChargeSignature, "inChargeSignature");

//             const payload = {
//                 ...formData,
//                 photo: updatedPhoto,
//                 signature: updatedSignature,
//                 declarationSignature: updatedDeclarationSignature,
//                 inChargeSignature: updatedInChargeSignature,
//                 updatedAt: serverTimestamp(),
//             };

//             const docRef = doc(db, "registration_form_data", id);
//             await updateDoc(docRef, payload);

//             alert("✅ Form successfully updated!");
//         } catch (error) {
//             console.error(error);
//             alert("❌ Error updating document.");
//         }
//     };

//     const handleNext = () => {
//         if (currentTab < TABS.length - 1) setCurrentTab(currentTab + 1);
//     };

//     const handlePrev = () => {
//         if (currentTab > 0) setCurrentTab(currentTab - 1);
//     };

//     if (loading) return <div>⏳ Loading form...</div>;

//     return (
//         <div style={{ maxWidth: "auto", margin: '0 auto', padding: 20, fontFamily: 'Arial' }}>
//             <h2 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "1rem" }}>Edit Form</h2>

//             <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
//                 {TABS.map((tab, idx) => (
//                     <button key={idx} onClick={() => setCurrentTab(idx)} style={{
//                         flex: 1, padding: "10px", background: currentTab === idx ? "#4A90E2" : "#ccc",
//                         color: "#fff", border: "none", borderRadius: 4
//                     }}>
//                         {tab}
//                     </button>
//                 ))}
//             </div>


//             <form onSubmit={(e) => e.preventDefault()}>
//                 {currentTab === 0 && (
//                     <div className="card p-4 shadow-sm">
//                         <h4 className="mb-4">Application Form</h4>
//                         <div className="row g-3">

//                             <div className="col-md-4">
//                                 <label htmlFor="applicantName" style={{ display: "flex" }}>Applicant Name</label>
//                                 <input type="text" name="applicantName" id="applicantName" value={formData.applicantName} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="course" style={{ display: "flex" }}>Course Applied For</label>
//                                 <select name="course" id="course" value={formData.course} onChange={handleChange} className="form-select">
//                                     <option value="">Select Course</option>
//                                     <option>Retail Sales Assistant</option>
//                                     <option>Warehouse Associate</option>
//                                     <option>Digital Mitra</option>
//                                     <option>IT Customer Care Executive</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="batch" style={{ display: "flex" }}>Preferred Batch</label>
//                                 <select name="batch" id="batch" value={formData.batch} onChange={handleChange} className="form-select">
//                                     <option value="">Select Batch</option>
//                                     <option>Morning</option>
//                                     <option>Afternoon</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="dob" style={{ display: "flex" }}>Date of Birth</label>
//                                 <input type="date" name="dob" id="dob" value={formData.dob} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="age" style={{ display: "flex" }}>Age</label>
//                                 <input type="number" name="age" id="age" value={formData.age} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="gender" style={{ display: "flex" }}>Gender</label>
//                                 <select name="gender" id="gender" value={formData.gender} onChange={handleChange} className="form-select">
//                                     <option value="">Select Gender</option>
//                                     <option>Male</option>
//                                     <option>Female</option>
//                                     <option>Other</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="qualification" style={{ display: "flex" }}>Qualification</label>
//                                 <input type="text" name="qualification" id="qualification" value={formData.qualification} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="contact1" style={{ display: "flex" }}>Contact No.</label>
//                                 <input type="text" name="contact1" id="contact1" value={formData.contact1} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="address" style={{ display: "flex" }}>Address</label>
//                                 <input type="text" name="address" id="address" value={formData.address} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="fatherName" style={{ display: "flex" }}>Father’s Name</label>
//                                 <input type="text" name="fatherName" id="fatherName" value={formData.fatherName} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="contact2" style={{ display: "flex" }}>Father’s Contact</label>
//                                 <input type="text" name="contact2" id="contact2" value={formData.contact2} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="fatherOccupation" style={{ display: "flex" }}>Father’s Occupation</label>
//                                 <input type="text" name="fatherOccupation" id="fatherOccupation" value={formData.fatherOccupation} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="motherName" style={{ display: "flex" }}>Mother’s Name</label>
//                                 <input type="text" name="motherName" id="motherName" value={formData.motherName} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="motherOccupation" style={{ display: "flex" }}>Mother’s Occupation</label>
//                                 <input type="text" name="motherOccupation" id="motherOccupation" value={formData.motherOccupation} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="familySize" style={{ display: "flex" }}>Family Size</label>
//                                 <input type="number" name="familySize" id="familySize" value={formData.familySize} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="familyType" style={{ display: "flex" }}>Type of Family</label>
//                                 <select name="familyType" id="familyType" value={formData.familyType} onChange={handleChange} className="form-select">
//                                     <option value="">Select Type</option>
//                                     <option>Nuclear</option>
//                                     <option>Joint</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="income" style={{ display: "flex" }}>Monthly Income</label>
//                                 <input type="text" name="income" id="income" value={formData.income} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="group" style={{ display: "flex" }}>Social Group</label>
//                                 <select name="group" id="group" value={formData.group} onChange={handleChange} className="form-select">
//                                     <option value="">Select</option>
//                                     <option>SC</option>
//                                     <option>ST</option>
//                                     <option>OBC</option>
//                                     <option>General</option>
//                                     <option>Others</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="documents" style={{ display: "flex" }}>Documents Attached</label>
//                                 <select name="documents" id="documents" value={formData.documents} onChange={handleChange} className="form-select">
//                                     <option value="">Select</option>
//                                     <option>10th</option>
//                                     <option>12th</option>
//                                     <option>Graduation</option>
//                                     <option>Aadhar Card</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="jobPreference" style={{ display: "flex" }}>Employment on Completion</label>
//                                 <select name="jobPreference" id="jobPreference" value={formData.jobPreference} onChange={handleChange} className="form-select">
//                                     <option value="">Select</option>
//                                     <option>Yes</option>
//                                     <option>No</option>
//                                 </select>
//                             </div>


//                             <div className="col-md-4">
//                                 <label htmlFor="doa" style={{ display: "flex" }}>Date of Application</label>
//                                 <input type="date" name="doa" id="doa" value={formData.doa} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="photo" style={{ display: "flex" }}>Upload Photo</label>
//                                 <input type="file" name="photo" id="photo" accept="image/*" onChange={handleChange} className="form-control" />
//                                 {formData.photo && <small className="text-muted">Selected: {formData.photo.name}</small>}
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="signature" style={{ display: "flex" }}>Upload Signature</label>
//                                 <input type="file" name="signature" id="signature" accept="image/*" onChange={handleChange} className="form-control" />
//                                 {formData.signature && <small className="text-muted">Selected: {formData.signature.name}</small>}
//                             </div>

//                         </div>
//                     </div>
//                 )}

//                 {currentTab === 1 && (
//                     <>
//                         <h4 className="mb-4">Declaration from Parent</h4>
//                         <div className="row g-3">
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Parent’s Name</label>
//                                 <input type="text" name="declarationName" value={formData.declarationName} onChange={handleChange} className="form-control" />
//                             </div>
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Child’s Name</label>
//                                 <input type="text" name="declarationChildName" value={formData.declarationChildName} onChange={handleChange} className="form-control" />
//                             </div>
//                             {/* <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Course (CSR Program)</label>
//                                 <input type="text" name="declarationCourse" value={formData.declarationCourse} onChange={handleChange} className="form-control" />
//                             </div> */}
//                             <div className="col-md-4">
//                                 <label htmlFor="declarationCourse" style={{ display: "flex" }}>Course (CSR Program)</label>
//                                 <select name="declarationCourse" id="declarationCourse" value={formData.declarationCourse} onChange={handleChange} className="form-select">
//                                     <option value="">Select Course</option>
//                                     <option>Retail Sales Assistant</option>
//                                     <option>Warehouse Associate</option>
//                                     <option>Digital Mitra</option>
//                                     <option>IT Customer Care Executive</option>
//                                 </select>
//                             </div>
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Date of Declaration</label>
//                                 <input type="date" name="declarationDate" value={formData.declarationDate} onChange={handleChange} className="form-control" />
//                             </div>
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Upload Parent’s Signature</label>
//                                 <input type="file" name="declarationSignature" accept="image/*" onChange={handleChange} className="form-control" />
//                                 {formData.declarationSignature && <small className="text-muted">Selected: {formData.declarationSignature.name}</small>}
//                             </div>
//                         </div>
//                     </>
//                 )}

//                 {currentTab === 2 && (
//                     <div className="card p-4 shadow-sm">
//                         <h4 className="mb-4">FOR OFFICE USE ONLY</h4>
//                         <div className="row g-3">

//                             {/* Training Centre */}
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Training Centre</label>
//                                 <select
//                                     name="centre"
//                                     value={formData.centre}
//                                     onChange={handleChange}
//                                     className="form-select"
//                                 >
//                                     <option value="">Select Centre</option>
//                                     <option value="Mansa">Mansa</option>
//                                     <option value="Bhiwandi">Bhiwandi</option>
//                                     <option value="Tumkur">Tumkur</option>
//                                 </select>
//                             </div>

//                             {/* Referred By */}
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Referred By</label>
//                                 <select
//                                     name="referredBy"
//                                     value={formData.referredBy}
//                                     onChange={handleChange}
//                                     className="form-select"
//                                 >
//                                     <option value="">Select</option>
//                                     <option value="Mobiliser">Mobiliser</option>
//                                     <option value="Trainer">Trainer</option>
//                                     <option value="Friend">Friend</option>
//                                 </select>
//                             </div>

//                             {/* Registration No */}
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Registration No.</label>
//                                 <input
//                                     type="text"
//                                     name="regNo"
//                                     value={formData.regNo}
//                                     onChange={handleChange}
//                                     className="form-control"
//                                 />
//                             </div>

//                             {/* Batch No */}
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Batch No.</label>
//                                 <input
//                                     type="text"
//                                     name="batchNo"
//                                     value={formData.batchNo}
//                                     onChange={handleChange}
//                                     className="form-control"
//                                 />
//                             </div>

//                             {/* Photocopy of required documents submitted */}
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Photocopy of Required Documents Submitted</label>
//                                 <select
//                                     name="docsSubmitted"
//                                     value={formData.docsSubmitted}
//                                     onChange={handleChange}
//                                     className="form-select"
//                                 >
//                                     <option value="">Select</option>
//                                     <option value="Yes">Yes</option>
//                                     <option value="No">No</option>
//                                 </select>
//                             </div>

//                             {/* Screening Schedule */}
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Screening Schedule</label>
//                                 <select
//                                     name="screenSchedule"
//                                     value={formData.screenSchedule || ""}
//                                     onChange={handleChange}
//                                     className="form-select"
//                                 >
//                                     <option value="">Select</option>
//                                     <option value="with applicant">With Applicant</option>
//                                     <option value="with parents">With Parents</option>
//                                 </select>
//                             </div>

//                             {/* Office Date */}
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Office Date</label>
//                                 <input
//                                     type="date"
//                                     name="officeDate"
//                                     value={formData.officeDate}
//                                     onChange={handleChange}
//                                     className="form-control"
//                                 />
//                             </div>

//                             {/* Centre In‑Charge Name */}
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Centre In‑Charge Name</label>
//                                 <input
//                                     type="text"
//                                     name="inChargeName"
//                                     value={formData.inChargeName || ""}
//                                     onChange={handleChange}
//                                     className="form-control"
//                                 />
//                             </div>

//                             {/* Upload Centre In‑Charge Signature */}
//                             <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Upload Centre In‑Charge Signature</label>
//                                 <input
//                                     type="file"
//                                     name="inChargeSignature"
//                                     accept="image/*"
//                                     onChange={handleChange}
//                                     className="form-control"
//                                 />
//                                 {formData.inChargeSignature && (
//                                     <small className="text-muted">
//                                         Selected: {formData.inChargeSignature.name || "1 file uploaded"}
//                                     </small>
//                                 )}
//                             </div>

//                         </div>
//                     </div>
//                 )}
//             </form>

//             <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
//                 {currentTab > 0 && <button onClick={handlePrev} style={{ padding: "10px 20px", background: "#6c757d", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>Previous</button>}
//                 {currentTab < TABS.length - 1 ? (
//                     <button onClick={handleNext} style={{ padding: "10px 20px", background: "#17a2b8", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>Next</button>
//                 ) : (
//                     <button onClick={handleUpdate} style={{ padding: "10px 20px", background: "#28a745", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>Update</button>
//                 )}
//             </div>
//         </div>
//     );
// };

// export default TabFormEdit;
